import React from "react";
import PropTypes from "prop-types";
import {FormattedMessage, injectIntl} from "react-intl";
import c from "classnames";
import config from "~/config";
import {RANGE_UNIT_TRANSLATIONS} from "~/util/ui-translations";
import {
    getAspectValueMatchedTo,
    HARD_SKILLS_GREEN,
    JOB_FUNCTIONS_GREEN,
    MatchedTo,
} from "~/util/match";
import ProgressBar from "~/components/ProgressBar";
import styles from "./styles.module.scss";
import baseStyles from "../styles.module.scss";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

class AspectRow extends React.PureComponent {
    static propTypes = {
        intl: PropTypes.object.isRequired,
        name: PropTypes.string.isRequired,
        reverseLabel: PropTypes.string.isRequired,
        reverseHelp: PropTypes.string.isRequired,
        matches: PropTypes.array,
        isFirstRow: PropTypes.bool.isRequired,
        getMatchDisplayValue: PropTypes.func.isRequired,
    };

    render() {
        return (
            <React.Fragment>
                {this.renderForward()}
                {this.renderReverse()}
            </React.Fragment>
        );
    }

    renderForward() {
        const {name, matches} = this.props;

        return (
            <tr className={styles.thickTopBorder}>
                <td className={baseStyles.stickyColumn}>
                    <FormattedMessage id={`aspect.${name}`} />
                </td>
                {matches.map((match, index) => this.renderMatch(match, index, "FORWARD"))}
            </tr>
        );
    }

    renderReverse() {
        const {intl, name, reverseLabel, reverseHelp, matches, isFirstRow} = this.props;

        if (ASPECTS_WITHOUT_REVERSE.includes(name)) {
            return null;
        }

        return (
            <tr>
                <td className={c(styles.reverseLabel, baseStyles.stickyColumn)}>
                    <FormattedMessage id={reverseLabel} />
                    {reverseHelp && isFirstRow && (
                        <span>
                            <br />
                            <i
                                title={intl.formatMessage({id: reverseHelp})}
                                className="fas fa-question-circle"
                            />
                        </span>
                    )}
                </td>
                {matches.map((match, index) => this.renderMatch(match, index, "REVERSE"))}
            </tr>
        );
    }

    renderMatch(match, index, scoreType) {
        const {name, getMatchDisplayValue} = this.props;

        const aspects = getMatchDisplayValue(match, "aspects");
        if (!aspects) return null;

        const details = filterByScoreType(match.details[name], scoreType);
        const aspect = aspects[name];

        if (details.length > 0) {
            return (
                <td key={index} className={baseStyles.matchColumn}>
                    {details.map((item, index) => this.renderDetailsItem(scoreType, item, index))}
                </td>
            );
        } else if (scoreType === "REVERSE") {
            return (
                <td key={index} className={baseStyles.matchColumn}>
                    {this.renderAspectLabels(aspect)}
                </td>
            );
        } else {
            return (
                <td key={index} className={baseStyles.matchColumn}>
                    {this.renderNotAvailable()}
                </td>
            );
        }
    }

    renderDetailsItem(scoreType, item, index) {
        const {name} = this.props;

        if (scoreType === "FORWARD" && ASPECTS_MATCHED_TO[name] !== undefined) {
            return <div key={index}>{this.renderMatchedToDetailsItem(item)}</div>;
        } else if (scoreType === "FORWARD") {
            return <div key={index}>{this.renderForwardDetailsItem(item)}</div>;
        } else {
            return <div key={index}>{this.renderReverseDetailsItem(item)}</div>;
        }
    }

    renderForwardDetailsItem(item) {
        const icon = item.found ? "check-circle" : "times-circle";
        const label = this.getLabelForItem(item);
        return this.renderSingleItem(icon, label, {score: item.score});
    }

    renderReverseDetailsItem(item) {
        const icon = item.found ? "check-circle" : "circle";
        const label = this.getLabelForItem(item);
        return this.renderSingleItem(icon, label, item.found ? {score: item.score} : {});
    }

    renderMatchedToDetailsItem(item) {
        const {name} = this.props;
        const matchedTo = getAspectValueMatchedTo(item, name);
        const icon = getIconForMatchedTo(matchedTo, ASPECTS_MATCHED_TO[name]);
        const label = this.getLabelForItem(item);
        return this.renderSingleItem(icon, label, {score: item.score, matchedTo});
    }

    getLabelForItem(item) {
        const {name} = this.props;

        return TRANSLATED_ASPECTS.includes(name) ? (
            <FormattedMessage id={`${name}.${item.label}`} defaultMessage={item.label} />
        ) : (
            item.label
        );
    }

    renderAspectLabels(aspect) {
        const labels = this.getAspectLabels(aspect);

        if (labels.length > 0) {
            return labels.map((label, index) => this.renderSingleItem("circle", label));
        } else {
            return this.renderNotAvailable();
        }
    }

    renderNotAvailable() {
        return this.renderSingleItem(
            "question-circle",
            <FormattedMessage id="matchQuality.notAvailable" />,
            {classNames: styles.notAvailable}
        );
    }

    getAspectLabels(aspect) {
        const {name} = this.props;
        const aspectType = config(`aspects.${name}.type`);

        switch (aspectType) {
            case "concepts":
            case "keywords":
                return aspect.value.map(v => v.label);

            case "locations":
                return aspect.value.map(
                    v => `${v.label} ≤ ${v.range}${RANGE_UNIT_TRANSLATIONS[v.rangeUnit] || ""}`
                );

            case "range":
                return aspect.value ? [`${aspect.value.from} – ${aspect.value.to}`] : [];

            case "float":
            case "integer":
                return aspect.value.value;

            default:
                return undefined;
        }
    }

    renderSingleItem(icon, label, {score, matchedTo, classNames} = {}) {
        let iconAndLabel = (
            <div>
                <i className={`far fa-${icon}`} />
                <span>{label}</span>
            </div>
        );

        if (
            matchedTo !== undefined &&
            matchedTo !== MatchedTo.NOTHING &&
            matchedTo !== MatchedTo.UNKNOWN
        ) {
            const messageId =
                matchedTo === MatchedTo.ASPECT
                    ? `matchedTo.${matchedTo}.${this.props.name}`
                    : `matchedTo.${matchedTo}`;

            iconAndLabel = (
                <OverlayTrigger
                    placement="right"
                    delay={{show: 250, hide: 400}}
                    overlay={props => (
                        <Tooltip {...props} show={props.show.toString()}>
                            <FormattedMessage id={messageId} />
                        </Tooltip>
                    )}
                >
                    {iconAndLabel}
                </OverlayTrigger>
            );
        }

        return (
            <div className={c(styles.item, classNames)}>
                {iconAndLabel}
                {score !== undefined && (
                    <ProgressBar
                        className={styles.itemProgress}
                        variant="success"
                        now={score}
                        min={0}
                        max={1}
                    />
                )}
            </div>
        );
    }
}

function filterByScoreType(details, scoreType) {
    return details ? details.filter(item => item.scoreType === scoreType) : [];
}

function getIconForMatchedTo(matchedTo, greenValues) {
    let icon = "question-circle";

    if (matchedTo === MatchedTo.NOTHING) {
        icon = "times-circle";
    } else if (greenValues.includes(matchedTo)) {
        icon = "check-circle";
    }

    return icon;
}

const TRANSLATED_ASPECTS = ["contractTypes", "workingHours"];

const ASPECTS_WITHOUT_REVERSE = ["locations"];

const ASPECTS_MATCHED_TO = {
    jobFunctions: JOB_FUNCTIONS_GREEN,
    hardSkills: HARD_SKILLS_GREEN,
};

export default injectIntl(AspectRow, {forwardRef: true});
